import '../color-selector/color-selector';
import '../product-sizes/product-sizes';
import '../../../../legacy-library/webcomponents/svg-icon/svg-icon';
import '../../../elements/prod-image/prod-image';
import '../../../elements/tessa-image/tessa-image';
import '../../../elements/price-asterisk/price-asterisk';

import { TemplateElement, html, toString, i18n } from '@webtides/element-js';
import { unsafeHTML } from 'lit-html/directives/unsafe-html';
import ColorSelectorEvent from '../color-selector/ColorSelectorEvent';
import FrameSelectorEvent from '../../frame-selector/FrameSelectorEvent';
import CurrencyFormatter from '../../../../js/helper/currency-formatter';
import LazySrcEvents from '../../../../legacy-library/webcomponents/lazy-src/LazySrcEvents';
import { define, SliderElementEvents } from '@webtides/slider-element';
import {
    getFormattedDiscountPrice,
    getFormattedSalePrice,
    getFormattedStrikePrice,
    getFormattedBasePrice,
    isFromPrice,
    isStrikePrice,
} from '../../../../js/helper/price-model-helper';

define();

import styles from './_product-card.css';
import ProductCardEvents from './ProductCardEvents';

export default class ProductCardClass extends TemplateElement {
    constructor(options = {}) {
        super(Object.assign({ shadowRender: false, autoUpdate: false, styles: [styles] }, options));
    }

    connected() {
        this.checkCurrentFrameIdValidity();
        this.resetProductIndex();
        this.requestUpdate();
    }

    afterUpdate() {
        //slider may need to reindex items due to rerender with more or less images
        this.$refs.imageSlider?.indexItems();
        this.$refs.imageSlider.goTo(this.currentProductIndex);
        this.dispatch('PRODUCTCARD_INITIALZED', null, true);
    }

    properties() {
        return {
            name: '',
            description: '',
            price: '',
            frames: {},
            currentFrameId: false,
            activeColorKey: false,
            currentProductIndex: 0,
            hasFrameSelector: true,
            shouldRenderColorSelector: true,
            shouldRenderFrameSelector: true,
            shouldRenderProductSizes: true,
            showSpacerElement: false,
        };
    }

    get currentFrame() {
        if (this.currentFrameId === false || this.frames.hasOwnProperty(this.currentFrameId) === false) {
            return this.frames[Object.keys(this.frames)[0]];
        }
        return this.frames[this.currentFrameId];
    }

    get currentFrameProduct() {
        return this.currentFrame.products ? this.currentFrame.products[this.currentProductIndex] : null;
    }

    get frameProducts() {
        return this.currentFrame.products || [];
    }

    get frameLink() {
        return this.currentFrameProduct ? this.currentFrameProduct.href : null;
    }

    get frameSiegel() {
        if (this.currentFrame.siegel && this.currentFrame.siegel.length > 0) {
            return this.currentFrame.siegel[0];
        }
        return false;
    }

    get frameKeys() {
        return Object.keys(this.frames);
    }

    get frameLabels() {
        return Object.entries(this.frames).map(([key, frame]) => frame.label);
    }

    get imageBreakpoints() {
        let width = 450;
        if (this.clientWidth > 500) {
            width = 700;
        }

        if (this.clientWidth > 800) {
            width = 1050;
        }

        return [
            {
                from: 0,
                variation: '3by2',
                width: width,
            },
        ];
    }

    get canSlideLeft() {
        return this.currentProductIndex > 0;
    }

    get canSlideRight() {
        return this.currentProductIndex < this.frameProducts.length - 1;
    }

    get defaultPromotionLogo() {
        return `${window.siteConfig().publicPath}/assets/images/promotion_logo_default.svg`;
    }

    get framePromotionLogo() {
        return this.currentFrame?.promotionLogo ?? this.defaultPromotionLogo ?? false;
    }

    resetProductIndex() {
        if (this.activeColorKey) {
            const colorIndex = this.currentFrame.products.findIndex(
                product => product.colorFilterKey === this.activeColorKey
            );

            if (colorIndex > -1) {
                this.currentProductIndex = colorIndex;
                return;
            }
        } else {
            const initiallySelectedProductIndex = this.frameProducts.findIndex(color => color.selected);

            if (initiallySelectedProductIndex > -1) {
                this.currentProductIndex = initiallySelectedProductIndex;
                return;
            }
        }

        this.currentProductIndex = 0;
    }

    checkCurrentFrameIdValidity() {
        // check if currentFrameId is invalid and should default to the first frame
        if (!this.currentFrameId || !this.frames.hasOwnProperty(this.currentFrameId)) {
            this.currentFrameId = Object.keys(this.frames)[0];
        }
    }

    watch() {
        return {
            name: () => {
                this.resetProductIndex();
                this.requestUpdate();
            },
            activeColorKey: async () => this.resetProductIndex(),
            currentFrameId: () => {
                this.checkCurrentFrameIdValidity();
                this.requestUpdate();
            },
            currentProductIndex: () => {
                if (this.shouldRenderColorSelector)
                    this.$refs.colorSelector.activeColorIndex = this.currentProductIndex;
                if (this.shouldRenderProductSizes)
                    this.$refs.productSizes.sizes = this.currentFrameProduct?.sizes ?? [];
                this.$refs.title.href = this.frameLink;
                this.requestUpdate();
            },
        };
    }

    events() {
        return {
            'slider-element': {
                [SliderElementEvents.SLIDER_RUN]: ev => {
                    this.currentProductIndex = ev.detail || 0;
                },
                touchstart: {
                    listener: ev => {
                        //disable to prevent double swipes when nested
                        ev.stopPropagation();
                    },
                    options: {
                        passive: true,
                    },
                },
            },
            'frame-selector': {
                click: ev => {
                    ev.preventDefault();
                },
                [FrameSelectorEvent.FRAME_CLICK]: ev => {
                    this.currentFrameId = ev.detail;
                },
            },
            'color-selector': {
                click: ev => {
                    ev.preventDefault();
                },
                [ColorSelectorEvent.COLOR_CLICK]: ev => {
                    this.currentProductIndex = ev.detail;
                },
            },
            '[ref=arrowNext]': {
                click: ev => {
                    ev.preventDefault();
                    this.$refs.imageSlider.next();
                },
            },
            '[ref=arrowPrev]': {
                click: ev => {
                    ev.preventDefault();
                    this.$refs.imageSlider.previous();
                },
            },
            '[ref=title]': {
                click: ev => {
                    this.dispatch(ProductCardEvents.PRODUCT_CLICK, { link: this.frameLink }, true);
                },
            },
            'prod-image': {
                [LazySrcEvents.LOZAD_LOAD]: () => {
                    const images = this.querySelectorAll('prod-image');
                    images.forEach(image => image.loadInstant());
                },
            },
        };
    }

    renderPrice() {
        return html`
            ${!!isFromPrice(this.currentFrame.prices) ? `${i18n('FROM')}` : ''}
            <span class="product-card-price">
                ${getFormattedSalePrice(this.currentFrame.prices)}<price-asterisk></price-asterisk
            ></span>
            ${!!getFormattedStrikePrice(this.currentFrame.prices)
                ? html`
                      &nbsp;
                      <del>${getFormattedStrikePrice(this.currentFrame.prices)}</del>&nbsp;${CurrencyFormatter.symbol()}
                      ${isStrikePrice(this.currentFrame.prices) ? i18n('RECOMMENDED_RETAIL_PRICE') : ''}
                  `
                : ''}
        `;
    }

    renderSaving() {
        if (!getFormattedDiscountPrice(this.currentFrame.prices)) {
            return '';
        }

        return html`
            <div
                class="corner absolute right-0 bottom-0 text-center uppercase text-sm text-grey-300 group-hover:text-brand-400"
            >
                ${isFromPrice(this.currentFrame.prices)
                    ? html` <div class="absolute from-price w-full top-0">${i18n('UP_TO')}</div> `
                    : ''}
                <div class="background pt-1 text-white h-full bg-brand-400 md:bg-grey-300 md:group-hover:bg-brand-400">
                    <div class="text-lg" style="line-height: 1;">
                        ${getFormattedDiscountPrice(this.currentFrame.prices, true)}
                        <span class="text-sm">${CurrencyFormatter.symbol()}</span>
                    </div>
                    ${i18n('productCard.saving', 'sparen')}
                </div>
            </div>
        `;
    }

    renderBasePrice() {
        return html`<span>${getFormattedBasePrice(this.currentFrame.prices)}</span>`;
    }

    getImageSlider() {
        return html`
            <slider-element
                ref="imageSlider"
                class="absolute inset-0"
                arrows="false"
                dots="false"
                selected-index="${this.currentProductIndex}"
            >
                ${this.frameProducts.map(({ image }, index) =>
                    image.source === 'TESSA'
                        ? html`
                              <tessa-image
                                  url="${image?.url}"
                                  class="block cursor-pointer px-2px item"
                                  img-css-class="object-scale-down w-full"
                              ></tessa-image>
                          `
                        : html`
                              <prod-image
                                  class="block cursor-pointer px-2px item"
                                  img-class="object-scale-down w-full"
                                  lazy-placeholder="true"
                                  breakpoints="${JSON.stringify(this.imageBreakpoints)}"
                                  src="${image?.url}"
                              ></prod-image>
                          `
                )}
            </slider-element>
        `;
    }

    getSliderArrows() {
        if (this.frameProducts.length <= 1) {
            return ``;
        }

        return html`
            ${this.canSlideLeft ?? false
                ? html`
                      <div
                          class="cursor-pointer absolute flex justify-center items-center py-2 px-0 top-0 left-0 w-4 xl:w-8 arrow text-grey-300
                                    text-center opacity-100 md:opacity-0 md:group-hover:opacity-100"
                          ref="arrowPrev"
                      >
                          <svg-icon name="prev" class="w-4 h-4"></svg-icon>
                      </div>
                  `
                : ''}
            ${this.canSlideRight ?? false
                ? html`
                      <div
                          class="cursor-pointer absolute flex justify-center items-center py-2 px-0 top-0 right-0 w-4 xl:w-8 arrow text-grey-300
                                    text-center opacity-100 md:opacity-0 md:group-hover:opacity-100"
                          ref="arrowNext"
                      >
                          <svg-icon name="next" class="w-4 h-4"></svg-icon>
                      </div>
                  `
                : ''}
        `;
    }

    renderFrameSelector() {
        return this.hasFrameSelector && this.frameKeys.length > 0 && this.shouldRenderFrameSelector
            ? html`
                  <frame-selector
                      class="inline-block"
                      frames="${JSON.stringify(this.frameKeys)}"
                      labels="${JSON.stringify(this.frameLabels)}"
                      active-frame="${this.currentFrameId}"
                  ></frame-selector>
              `
            : '';
    }

    renderTagline() {
        return html``;
    }

    renderProductSizes() {
        return this.shouldRenderProductSizes
            ? html` <product-sizes
                  ref="productSizes"
                  class="sizes flex justify-center mt-2 md:opacity-0 md:group-hover:opacity-100"
                  .sizes=${this.currentFrameProduct?.sizes ?? []}
              ></product-sizes>`
            : ``;
    }

    renderProductDescription() {
        return this.description
            ? html` <div class="product-card-description mb-1 w-full truncate">${unsafeHTML(this.description)}</div>`
            : ``;
    }

    renderColorSelector() {
        return this.shouldRenderColorSelector
            ? html` <color-selector
                  ref="colorSelector"
                  class="block mt-5"
                  active-color-index="${this.currentProductIndex}"
                  colors="${JSON.stringify(this.frameProducts)}"
              ></color-selector>`
            : ``;
    }

    renderSpacerElement() {
        return html``;
    }

    template() {
        return html`
            <a href="${this.frameLink}" class="block product-card-content group p-4 xl:p-8 relative overflow-hidden">
                ${this.renderTagline()}
                <div class="product-card-title mb-1" ref="title">${unsafeHTML(this.name)}</div>
                ${this.renderProductDescription()}
                <div class="product-card-price-group">${this.renderPrice()} &nbsp; ${this.renderBasePrice()}</div>
                ${this.renderFrameSelector()}
                <div class="relative slider-wrapper" role="img" alt="${this.name}">
                    ${this.getImageSlider()}
                    ${this.frameSiegel
                        ? html`
                              <div class="absolute seal">
                                  <img src="${this.frameSiegel}" />
                              </div>
                          `
                        : ''}
                    ${this.currentFrame?.promotionLogo
                        ? html`
                              <img
                                  class="promotion-logo absolute top-0 right-0"
                                  src="${this.framePromotionLogo}"
                                  alt="Promotion logo"
                              />
                          `
                        : ``}
                    ${this.getSliderArrows()}
                </div>

                ${this.renderColorSelector()} ${this.renderProductSizes()} ${this.renderSpacerElement()}
                ${this.renderSaving()}
            </a>
        `;
    }
}
